/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCallbackProfileList.ts (Callback Profiles)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Callback Profiles list state and logic.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchCallbackProfiles } from '../api/callbackProfilesApi';
import { CallbackProfileResponse } from '../types';
import { ListCallbackProfilesParams } from '../types/callbackProfiles.types';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';

interface UseCallbackProfileListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Callback Profiles list
 * Implements Domain Contextual Filtering pattern
 */
export function useCallbackProfileList(options: UseCallbackProfileListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListCallbackProfilesParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        domainId: effectiveDomainId
      };

      const result = await fetchCallbackProfiles(queryParams);

      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('callbackProfiles.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('callbackProfiles.columns.name'), visible: true, sortable: true },
    { key: 'description', label: t('callbackProfiles.columns.description'), visible: true, sortable: false },
    { key: 'enabled', label: t('callbackProfiles.columns.enabled'), visible: true, sortable: true },
    { key: 'updatedAt', label: t('callbackProfiles.columns.updatedAt'), visible: true, sortable: true },
    { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'callback-profiles',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as CallbackProfileResponse[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

